/**
 * CitationHighlighter - Right panel for file preview with citation highlighting
 *
 * Shows the source document with highlighted sections when citations are clicked.
 *
 * IMPORTANT: This component uses the preview API endpoint which returns
 * EXTRACTED TEXT ONLY - never raw binary file content.
 *
 * @module CitationHighlighter
 */

import React, { useState, useEffect, useRef } from 'react';

import {
  X,
  FileText,
  ZoomIn,
  ZoomOut,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  BookOpen,
  Maximize2,
  Minimize2,
  AlertTriangle,
  Clock,
  Cloud,
  HardDrive,
} from 'lucide-react';

import ideasKitchenApi from '@/pages/ideas-kitchen/api/ideasKitchenApi';

const CitationHighlighter = ({ file, citation, onClose, notebookId }) => {
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const contentRef = useRef(null);

  // Load file preview from API (EXTRACTED TEXT ONLY)
  useEffect(() => {
    if (file?.id && notebookId) {
      loadPreview();
    }
  }, [file?.id, notebookId]);

  // Scroll to citation when it changes
  useEffect(() => {
    if (citation && contentRef.current) {
      // In a real implementation, you'd scroll to the specific text/page
      // For now, just scroll to top
      contentRef.current.scrollTop = 0;
    }
  }, [citation]);

  const loadPreview = async () => {
    if (!file || !notebookId) return;

    setIsLoading(true);
    setError(null);

    try {
      // ALWAYS use the preview API - it returns extracted text ONLY, never raw bytes
      const response = await ideasKitchenApi.getFilePreview(notebookId, file.id);

      if (!response?.success) {
        setPreview({ type: 'error', content: response?.message || 'Preview not available' });
        return;
      }

      // Backend contract:
      // - { success:true, status, preview:null, message } while processing/failed
      // - { success:true, status:'ready', preview:{ text, length, provider, ocrProvider } } when ready
      if (response.status !== 'ready') {
        const isFailed = response.status === 'failed';
        setPreview({
          type: isFailed ? 'error' : 'processing',
          content:
            response.message ||
            (isFailed ? 'Processing failed.' : 'Document is being processed. Please wait...'),
          status: response.status,
        });
        return;
      }

      const p = response.preview || {};
      setPreview({
        type: 'text',
        content: p.text || '[No content extracted]',
        extractionProvider: p.provider,
        ocrProvider: p.ocrProvider,
      });
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load file preview');
      setPreview({
        type: 'error',
        content: err?.message || err?.error || 'Failed to load preview',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));

  const highlightText = (content, searchText) => {
    if (!searchText) return content;

    // Simple text highlighting - in production use proper text matching
    const regex = new RegExp(`(${escapeRegex(searchText)})`, 'gi');
    return content.replace(regex, '<mark class="bg-amber-200 dark:bg-amber-800">$1</mark>');
  };

  const escapeRegex = string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  return (
    <div
      className={`h-full flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-neutral-900' : ''}`}
    >
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-3 border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 dark:text-white truncate">
              {file?.displayName || 'Document'}
            </p>
            <p className="text-[10px] text-slate-500 dark:text-neutral-400">
              {totalPages > 1 ? `Page ${currentPage} of ${totalPages}` : 'Preview'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4 text-slate-500" />
            ) : (
              <Maximize2 className="h-4 w-4 text-slate-500" />
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Citation indicator */}
      {citation && (
        <div className="flex-shrink-0 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Citation source highlighted
              {citation.excerpt && (
                <span className="text-amber-500 dark:text-amber-400 ml-1">
                  — "{citation.excerpt.substring(0, 50)}..."
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800">
        {/* Pagination */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-slate-600 dark:text-neutral-400" />
          </button>
          <span className="text-xs text-slate-600 dark:text-neutral-400 min-w-[60px] text-center">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-slate-600 dark:text-neutral-400" />
          </button>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleZoomOut}
            disabled={zoom <= 50}
            className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ZoomOut className="h-4 w-4 text-slate-600 dark:text-neutral-400" />
          </button>
          <span className="text-xs text-slate-600 dark:text-neutral-400 min-w-[40px] text-center">
            {zoom}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={zoom >= 200}
            className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ZoomIn className="h-4 w-4 text-slate-600 dark:text-neutral-400" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div ref={contentRef} className="flex-1 overflow-auto p-4 bg-slate-100 dark:bg-neutral-950">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
              <p className="text-sm text-slate-500 dark:text-neutral-400">Loading preview...</p>
            </div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-300 dark:text-red-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500 dark:text-neutral-400">{error}</p>
            </div>
          </div>
        ) : preview?.type === 'processing' ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center bg-white dark:bg-neutral-800 rounded-xl shadow-sm p-8 max-w-md">
              <Clock className="h-12 w-12 text-blue-400 mx-auto mb-4 animate-pulse" />
              <h3 className="text-lg font-medium text-slate-700 dark:text-neutral-300 mb-2">
                Processing Document
              </h3>
              <p className="text-sm text-slate-500 dark:text-neutral-400 mb-4">{preview.content}</p>
              <div className="flex items-center justify-center gap-2 text-xs text-blue-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Extraction in progress...</span>
              </div>
            </div>
          </div>
        ) : preview?.type === 'error' ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center bg-white dark:bg-neutral-800 rounded-xl shadow-sm p-8 max-w-md">
              <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 dark:text-neutral-300 mb-2">
                Preview Unavailable
              </h3>
              <p className="text-sm text-slate-500 dark:text-neutral-400">{preview.content}</p>
            </div>
          </div>
        ) : preview?.type === 'text' ? (
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm min-h-full">
            {/* Provider Badge */}
            {(preview.ocrProvider || preview.extractionProvider) && (
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-neutral-700">
                {preview.ocrProvider === 'google_vision' ||
                preview.extractionProvider === 'google_vision' ? (
                  <>
                    <Cloud className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-xs text-emerald-600 dark:text-emerald-400">
                      Google-Processed
                    </span>
                  </>
                ) : (
                  <>
                    <HardDrive className="h-3.5 w-3.5 text-amber-500" />
                    <span className="text-xs text-amber-600 dark:text-amber-400">
                      Processed locally
                    </span>
                  </>
                )}
              </div>
            )}
            {/* Text Content */}
            <div
              className="p-6"
              style={{
                fontSize: `${zoom / 100}rem`,
                lineHeight: 1.6,
              }}
            >
              <pre
                className="whitespace-pre-wrap font-sans text-slate-700 dark:text-neutral-300"
                dangerouslySetInnerHTML={{
                  __html: citation?.excerpt
                    ? highlightText(preview.content, citation.excerpt)
                    : preview.content,
                }}
              />
            </div>
          </div>
        ) : preview?.type === 'image' ? (
          <div className="flex items-center justify-center h-full">
            <img
              src={preview.url}
              alt={file?.displayName}
              className="max-w-full max-h-full object-contain rounded-lg shadow-md"
              style={{ transform: `scale(${zoom / 100})` }}
            />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <FileText className="h-12 w-12 text-slate-300 dark:text-neutral-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500 dark:text-neutral-400">
                Preview not available for this file type
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-3 py-2 border-t border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <p className="text-[10px] text-slate-400 dark:text-neutral-500 text-center">
          ⚡ Click citations in responses to highlight source text
        </p>
      </div>
    </div>
  );
};

export default CitationHighlighter;
